import { useMemo } from 'react';
import type {
  ChartAxesConfig,
  HorizontalBarDatum,
  LegendDisplayMode,
  ValueMode,
} from '../types';
import {
  buildValueFormatter,
  computeAxisMax,
  computeAxisMin,
} from '../controllers/axisController';

interface UseChartAxesArgs {
  data: readonly HorizontalBarDatum[];
  valueMode: ValueMode;
  legendDisplay: LegendDisplayMode;
  yAxisLabelWidth: number;
  xAxisLabel?: string;
}

/**
 * Builds the axis configuration for the horizontal bar chart.
 *
 * The y-axis band is keyed by `id`, so its formatter receives a datum id rather
 * than a position: it has to look the name up by id. Indexing `data` with that
 * value only lines up while the ids happen to be 0..n-1, and silently shifts or
 * blanks every label otherwise.
 *
 * A count axis is stepped in whole units. Left to itself a small series produces
 * fractional ticks, and rounding only the label then repeats it -- 0, 0, 1, 1 --
 * which reads as a broken scale.
 */
export const useChartAxes = ({
  data,
  valueMode,
  legendDisplay,
  yAxisLabelWidth,
  xAxisLabel,
}: UseChartAxesArgs): ChartAxesConfig => {
  return useMemo(() => {
    const valueFormatter = buildValueFormatter(valueMode);
    const max = computeAxisMax(data, valueMode);
    const min = computeAxisMin(valueMode);
    const showLabels = legendDisplay === 'axis';

    return {
      xAxis: {
        height: 50,
        ...(min !== undefined ? { min } : {}),
        ...(max !== undefined ? { max } : {}),
        ...(valueMode === 'count' ? { tickMinStep: 1 } : {}),
        label: xAxisLabel,
        valueFormatter: (value: number) => {
          if (typeof value !== 'number') return '';
          return valueFormatter(value);
        },
        tickLabelInterval: () => true,
      },
      yAxis: {
        width: showLabels ? yAxisLabelWidth : 0,
        valueFormatter: (id: string | number) => {
          if (!showLabels) return '';
          return data.find((datum) => datum.id === id)?.name ?? '';
        },
        showLabels,
      },
    };
  }, [data, valueMode, legendDisplay, yAxisLabelWidth, xAxisLabel]);
};
